/**
 * 3D ビューの上に重ねる部品（ツール・レイヤー・凡例・操作ヘルプ・架空の札）と、API キーの画面。
 * 文言・並び・色はサンプル（WebUI-Sample/3D-Portal-Google3D.html の renderTools・renderLegend・
 * keyDlg）と同じ。描画ライブラリに依存しない。
 */
import { escapeHtml as esc } from '../core/text';

export interface ChromeState {
  mode: 'nation' | 'site';
  /** Google 実写 3D につながっているか（ボタンを出すか）と、いま表示しているか。 */
  google: boolean;
  useG: boolean;
  autoRotate: boolean;
  layers: Readonly<Record<string, boolean>>;
}

export function toolsHtml(c: ChromeState): string {
  return c.mode === 'site'
    ? `<button class="back" data-view="nation">◀ 全国へ</button><button data-view="site">現場正面</button><button data-view="bird">俯瞰</button><button data-view="top">真上</button><button data-view="eye">水平</button><button data-view="spin" class="${c.autoRotate ? 'on' : ''}">自動回転</button>`
    : `<button data-view="all">日本全体</button><button data-view="top">真上</button><button data-view="bird">斜め</button>`;
}

const SITE_LAYERS: [string, string][] = [
  ['heat', '切盛ヒートマップ'],
  ['design', '計画面'],
  ['equip', '重機'],
  ['route', '搬出ルート'],
  ['crane', '揚重範囲'],
  ['labels', 'ラベル'],
];
const NATION_LAYERS: [string, string][] = [
  ['roads', '県境・高速道路'],
  ['labels', 'ラベル'],
];

export function layersHtml(c: ChromeState): string {
  const g3 = c.google
    ? `<button data-layer="g3d" class="${c.layers.g3d ? 'on' : ''}" title="Google 実写3D の表示／非表示">Google 3D</button>`
    : '';
  const list = c.mode === 'site' ? SITE_LAYERS : NATION_LAYERS;
  return (
    g3 +
    list
      .map(([k, n]) => `<button data-layer="${k}" class="${c.layers[k] ? 'on' : ''}">${n}</button>`)
      .join('')
  );
}

export function helpHtml(c: ChromeState): string {
  return c.mode === 'site'
    ? '<b>左ドラッグ</b> 現場の周りを回る　<b>右ドラッグ/ホイール</b> ズーム<br><b>中ドラッグ/Ctrl+ドラッグ</b> 傾き　<b>Esc</b> 全国へ　<b>Space</b> 再生'
    : '<b>ピン/ラベル</b> 現場へ　<b>都道府県クリック</b> 絞り込み<br><b>左ドラッグ</b> 移動　<b>右ドラッグ/ホイール</b> ズーム　<b>Ctrl+ドラッグ</b> 傾き';
}

/** 現場表示のときだけ出す「架空」の札（FR-VIEW-09）。全国表示では null（隠す）。 */
export function fictionText(c: ChromeState): string | null {
  if (c.mode !== 'site') return null;
  return c.useG
    ? '工事・建物計画はすべて架空のサンプルです／周辺の街並みは Google の実写3D（実在）'
    : '工事・建物計画はすべて架空のサンプルです';
}

const sw = (style: string, label: string) =>
  `<div><span class="sw" style="${style}"></span>${label}</div>`;

export function legendHtml(c: ChromeState): string {
  if (c.mode === 'nation') {
    return [
      sw('background:#22c55e', '計画内'),
      sw('background:#f59e0b', '遅延予測 1〜14日'),
      sw('background:#ef4444', '遅延予測 15日超'),
      sw('background:#f8fafc;border:1px solid #64748b', '県境（広域表示時）'),
      sw('background:#e07b39', '高速道路（広域表示時）'),
    ].join('');
  }
  const bg = c.useG
    ? sw('background:linear-gradient(90deg,#7d8a6a,#a9a39a,#6f7f94)', '周辺：Google 実写3D（実在）')
    : sw('background:#b9c99a', '周辺：標高データの地形（1km）');
  return (
    bg +
    (c.layers.heat
      ? [
          sw('background:#dc2626', '切土（現況が計画面より高い）'),
          sw('background:#2563eb', '盛土（現況が計画面より低い）'),
          sw('background:#f8fafc', '切盛なし'),
        ].join('')
      : [
          sw('background:#a3b77f', '現況地盤（標高データ）'),
          sw('background:#b98d55', '土工中（施工フロント）'),
          sw('background:#d6c8a8', '計画地盤高で仕上がり'),
          sw('background:#b8bcc2', '躯体完了'),
          sw('background:linear-gradient(90deg,#9cc0fb,#c7b8f9)', '外装完了（現場色）'),
          sw('background:#f59e0b', '施工中'),
          sw(
            'background:rgba(120,160,230,.35);border:1px solid #93b4ef',
            '未施工（計画ボリューム）',
          ),
          sw(
            'background:repeating-linear-gradient(0deg,#a3b77f 0 3px,#6b7a52 3px 4px)',
            '等高線（実標高）',
          ),
        ].join(''))
  );
}

/** API キーの画面（サンプルの keyDlg。文言は元のまま）。 */
export function keyDialogHtml(): string {
  return `<div class="dlg" id="keyDlg" hidden>
  <div class="dlgBox" role="dialog" aria-modal="true" aria-labelledby="keyTtl">
    <h2 id="keyTtl">Google 実写3D（Photorealistic 3D Tiles）を使う</h2>
    <p>現場周辺の街並み・地形を Google の実写3Dで表示します。Google Cloud で発行した <b>Map Tiles API</b> のキーを入力してください。キーはこのブラウザにだけ保存され、ファイルには書き込まれません。</p>
    <div class="keyDiag" id="keyDiag" hidden></div>
    <label for="keyIn">APIキー<input id="keyIn" type="password" autocomplete="off" spellcheck="false" placeholder="AIza…"></label>
    <label class="ck"><input type="checkbox" id="keySave" checked> このブラウザに保存する（共有PCでは外してください）</label>
    <ul class="dlgNote">
      <li>キーは <b>API制限：Map Tiles API のみ</b>、<b>アプリ制限：HTTPリファラ（このサイトを <code>https://ホスト名/*</code> の形で）</b> にしてください。</li>
      <li>課金の暴走を防ぐため、Cloud Console で <b>1日あたりのクォータ上限</b> と <b>予算アラート</b> を設定してください（無料枠を超えると従量課金）。</li>
      <li>Google の3Dデータはオンライン表示のみです（保存・キャッシュ・抽出は規約で禁止）。画面左下の帰属表示は消さないでください。</li>
      <li>工事・建物計画はすべて架空です。実在の建物・土地とは関係ありません。</li>
    </ul>
    <div class="btns"><button class="btn" id="keyOk">Google 3D で表示</button><button class="btn ghost" id="keySkip">キーなし（簡易地形）で表示</button><button class="btn ghost" id="keyForget" hidden>保存したキーを削除</button></div>
    <p class="keyErr" id="keyErr" hidden></p>
  </div>
</div>`;
}

/** 接続できなかった原因の表示。手順の文は信頼できる固定の文言（HTML を含む）、題・詳細・URL はエスケープする。 */
export function keyDiagHtml(
  d: { title: string; steps: string[]; raw: string },
  pageUrl: string,
): string {
  return `<b>接続できなかった原因：${esc(d.title)}</b><ol>${d.steps.map((t) => `<li>${t}</li>`).join('')}</ol><div class="diagRaw">このページのURL：<code>${esc(pageUrl)}</code><br>詳細：${esc(d.raw || '—')}</div>`;
}

/** キーの形（英数字・_・- の 20〜80 文字。CSSP-SPEC-001 §8）。 */
export const KEY_RE = /^[A-Za-z0-9_-]{20,80}$/;
/** ブラウザに保存する名前（CSSP-SPEC-001 §7.3。サンプルの site-portal-google3d-key から変える。V4）。 */
export const KEY_STORE = 'cssp:google-map-tiles-key';
